import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card'
import { ArrowLeft, EnvelopeSimple, Buildings, CheckCircle } from '@phosphor-icons/react'
import { getAllCompanies } from '../../services/companies'
import { listTenants } from '../../services/tenants'
import { createInvite } from '../../services/users'

export default function InviteUserPage() {
  const navigate = useNavigate()
  const { showSuccess, showError } = useDialog()
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [companies, setCompanies] = useState<any[]>([])
  const [tenants, setTenants] = useState<any[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState({
    email: '',
    companyId: '',
    companyType: '' as 'shipper' | 'freight-forwarder' | '',
    role: 'user',
    tenantId: ''
  })

  useEffect(() => {
    async function loadData() {
      try {
        const [companiesData, tenantsData] = await Promise.all([
          getAllCompanies(),
          listTenants()
        ])
        setCompanies(companiesData)
        setTenants(tenantsData)
      } catch (error) {
        console.error('Error loading data:', error)
        showError('Erro', 'Erro ao carregar dados')
      } finally {
        setLoadingData(false)
      }
    }
    loadData()
  }, [])

  // Auto-select tenant when company is selected
  useEffect(() => {
    if (formData.companyId) {
      const company = companies.find(c => c.companyId === formData.companyId)
      if (company) {
        setFormData(prev => ({ ...prev, tenantId: company.tenantId }))
      }
    }
  }, [formData.companyId, companies])

  function validateForm() {
    const newErrors: Record<string, string> = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    if (!formData.companyId) {
      newErrors.companyId = 'Empresa é obrigatória'
    }

    if (!formData.role) {
      newErrors.role = 'Função é obrigatória'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)

      await createInvite({
        email: formData.email,
        tenantId: formData.tenantId,
        companyId: formData.companyId,
        companyType: formData.companyType as 'shipper' | 'freight-forwarder',
        role: formData.role
      })

      showSuccess('Sucesso', `Convite enviado com sucesso para ${formData.email}!`)
      navigate('/master/users')
    } catch (error: any) {
      console.error('Error creating invite:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao criar convite')
    } finally {
      setLoading(false)
    }
  }

  if (loadingData) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/users')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Convidar Usuário</h1>
                <p className="text-sm text-gray-500">Enviar convite para se juntar a uma empresa</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Email */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <EnvelopeSimple size={20} className="text-emerald-600" weight="duotone" />
                  </div>
                  <div>
                    <CardTitle>Email do Usuário</CardTitle>
                    <CardDescription>
                      Informe o email para onde será enviado o convite
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="usuario@empresa.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Company Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Buildings size={20} className="text-blue-600" weight="duotone" />
                  </div>
                  <div>
                    <CardTitle>Empresa</CardTitle>
                    <CardDescription>
                      Selecione a empresa onde o usuário irá trabalhar
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="companyId" className="block text-sm font-medium text-gray-700 mb-2">
                      Empresa <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="companyId"
                      value={formData.companyId}
                      onChange={(e) => {
                        const selectedCompany = companies.find(c => c.companyId === e.target.value)
                        setFormData({
                          ...formData,
                          companyId: e.target.value,
                          companyType: selectedCompany?.companyType || ''
                        })
                      }}
                      className={`w-full px-3 py-2 border rounded-md ${errors.companyId ? 'border-red-500' : 'border-gray-200'}`}
                    >
                      <option value="">Selecione uma empresa</option>
                      {companies.map(company => (
                        <option key={company.companyId} value={company.companyId}>
                          {company.tradingName || company.name}
                          {' - '}
                          {company.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                          {' - '}
                          {company.countryCode}
                        </option>
                      ))}
                    </select>
                    {errors.companyId && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyId}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                      Função <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-md ${errors.role ? 'border-red-500' : 'border-gray-200'}`}
                    >
                      <option value="user">Usuário</option>
                      <option value="admin">Administrador</option>
                      <option value="manager">Gerente</option>
                    </select>
                    {errors.role && (
                      <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Define o nível de acesso do usuário na empresa
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Como funciona o convite?</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>O usuário receberá um email com link de convite</li>
                      <li>O convite expira em 7 dias</li>
                      <li>Ao aceitar, o usuário será vinculado à empresa selecionada</li>
                      <li>Se o usuário já tiver conta, ele apenas aceitará o convite</li>
                      <li>Se não tiver, ele criará uma conta no processo</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/master/users')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Enviando...' : 'Enviar Convite'}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </AppLayout>
  )
}
